import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/layout/Layout";
import { TrustBadges } from "../components/TrustBadges";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { formatMoney, toNumber } from "../lib/safeData";
import {
  CheckCircle2,
  Package,
  Clock,
  ArrowRight,
  ShoppingBag,
  Mail,
  FileText,
  AlertCircle,
} from "lucide-react";

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const orderId = searchParams.get("order_id");

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/orders/${orderId}`, {
          withCredentials: true,
        });
        setOrder(response.data);
      } catch (err) {
        console.error("Error fetching order:", err);
        setError("Could not load order details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const getStatusInfo = (status) => {
    switch (status) {
      case "PAID_LOCKED":
        return {
          icon: CheckCircle2,
          text: "Payment Confirmed",
          color: "text-green-600",
          bg: "bg-green-100",
        };
      case "PENDING_VERIFICATION":
        return {
          icon: Clock,
          text: "Awaiting Verification",
          color: "text-amber-600",
          bg: "bg-amber-100",
        };
      default:
        return {
          icon: Package,
          text: "Processing",
          color: "text-blue-600",
          bg: "bg-blue-100",
        };
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="space-y-6">
            <Skeleton className="h-12 w-48 mx-auto" />
            <Skeleton className="h-6 w-64 mx-auto" />
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!orderId) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 mx-auto text-slate-400 mb-6" />
          <h1 className="text-2xl font-bold text-slate-900 mb-3">No order found</h1>
          <p className="text-slate-600 mb-6">
            No order ID was provided. Check your orders or continue shopping.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/orders")} className="bg-slate-900 hover:bg-slate-800">
              <Package className="h-4 w-4 mr-2" />
              My Orders
            </Button>
            <Button variant="outline" onClick={() => navigate("/products")}>
              <ShoppingBag className="h-4 w-4 mr-2" />
              Continue Shopping
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-12 sm:py-16">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
            data-testid="confirmation-title"
          >
            Order Received!
          </h1>
          <p className="text-slate-600 text-lg">
            Thank you for shopping with CheepSweepz Concierge
          </p>
        </div>

        {/* Trust Badges - Prominent Position */}
        <TrustBadges variant="checkout" className="mb-8" data-testid="confirmation-trust-badges" />

        {/* Order Info Card */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden mb-8">
          {/* Order ID Header */}
          <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Order ID</p>
                <p
                  className="font-mono font-bold text-slate-900"
                  data-testid="order-id"
                >
                  {orderId || "N/A"}
                </p>
              </div>
              {order && (
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                    getStatusInfo(order.status).bg
                  }`}
                >
                  {(() => {
                    const StatusIcon = getStatusInfo(order.status).icon;
                    return (
                      <StatusIcon
                        className={`h-4 w-4 ${getStatusInfo(order.status).color}`}
                      />
                    );
                  })()}
                  <span
                    className={`text-sm font-medium ${
                      getStatusInfo(order.status).color
                    }`}
                  >
                    {getStatusInfo(order.status).text}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Order Details */}
          <div className="px-6 py-5">
            {error ? (
              <div className="flex items-center gap-3 text-amber-600 bg-amber-50 p-4 rounded-lg">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            ) : order ? (
              <div className="space-y-4">
                {/* Items Summary */}
                <div>
                  <p className="text-sm text-slate-500 mb-2">Items</p>
                  <p className="font-medium text-slate-900">
                    {order.items?.length || 0} item(s) in this order
                  </p>
                </div>

                {/* Total */}
                {toNumber(order.total_price ?? order.total) !== null && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Total Paid</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatMoney(order.total_price ?? order.total, order.currency)}
                    </p>
                    <p className="text-xs text-slate-500">
                      All fees included (customs, shipping, taxes)
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-600">
                Your order has been received and is being processed.
              </p>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8">
          <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-orange-500" />
            What Happens Next?
          </h2>
          <ol className="space-y-3 text-sm text-slate-600">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                1
              </span>
              <span>
                <strong className="text-slate-900">Order Confirmation:</strong>{" "}
                You&apos;ll receive an email confirmation shortly.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                2
              </span>
              <span>
                <strong className="text-slate-900">Processing:</strong> We
                purchase your items from the marketplace and prepare them for
                shipping.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                3
              </span>
              <span>
                <strong className="text-slate-900">Customs & Shipping:</strong>{" "}
                We handle all customs clearance and international shipping.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                4
              </span>
              <span>
                <strong className="text-slate-900">Delivery:</strong> Your
                package arrives at your doorstep!
              </span>
            </li>
          </ol>
        </div>

        {/* Disclaimer */}
        <div className="text-center text-sm text-slate-500 mb-8 px-4">
          <p>
            Questions about your order? Contact us at{" "}
            <a
              href="mailto:cheepsweepz@gmail.com"
              className="text-orange-600 hover:underline"
            >
              cheepsweepz@gmail.com
            </a>{" "}
            or call{" "}
            <a href="tel:+12464273323" className="text-orange-600 hover:underline">
              +1 246 427 3323
            </a>
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {orderId && (
            <Button
              onClick={() => navigate(`/orders/${orderId}`)}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="view-order-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              View Order Details
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate("/products")}
            data-testid="continue-shopping-btn"
          >
            <ShoppingBag className="h-4 w-4 mr-2" />
            Continue Shopping
          </Button>
        </div>
      </div>
    </Layout>
  );
}
